import { Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Box, CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Projects from './pages/Projects';
import Services from './pages/Services';
import Faq from './pages/Faq';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';
import Seo from './components/Seo';

import theme from './theme/theme';
import { light, dark, fullHeight, FONT } from './theme/tokens';

// Variabilele de temă, emise o singură dată pentru tot documentul. Schema
// închisă rescrie doar valorile, nu și regulile — nicio componentă nu trebuie
// să știe în ce mod suntem.
//
// Selectorii sunt exact cei pe care îi folosește și MUI cu
// `colorSchemeSelector: 'data'` (atributele `data-light` / `data-dark` pe
// `<html>`), ca tokenii noștri și paleta bibliotecii să se schimbe în același
// cadru. `:root` singur ține schema deschisă ca valoare implicită, dacă
// atributul lipsește din orice motiv.
const tokenStyles = {
  ':root, [data-light]': { ...light, colorScheme: 'light' },
  '[data-dark]': { ...dark, colorScheme: 'dark' },
};

// Fără asta, o navigare de la mijlocul paginii Servicii spre Contact aterizează
// tot la mijloc, pe un ecran care pare gol. Ancorele (#) rămân neatinse.
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;
    // `instant` anulează `scroll-behavior: smooth` din index.css: la schimbarea
    // paginii vrem saltul, nu o derulare animată prin tot conținutul vechi.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash]);

  return null;
}

// Ce ține de cromul browserului, nu de pagină: culoarea barei de sistem (bara
// de adrese pe Android, marginile paginii pe iOS) și `color-scheme`, din care
// browserul își colorează barele de derulare și controalele native.
//
// Ambele sunt puse întâi de scriptul din index.html, înainte de primul cadru.
// De acolo încolo trebuie ținute la zi de aici: scriptul le scrie ca stil în
// linie pe `<html>`, iar stilul în linie bate orice regulă din foaia de stil —
// fără efectul ăsta, cine comută pe luminos rămâne cu barele de derulare
// întunecate.
const BAR_COLOR = { light: '#FFFFFF', dark: '#000000' };

function SystemChrome() {
  const { mode, systemMode } = useColorScheme();
  const resolved = mode === 'system' ? systemMode : mode;

  useEffect(() => {
    if (!resolved) return;
    document.documentElement.style.colorScheme = resolved;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', BAR_COLOR[resolved] || BAR_COLOR.light);
  }, [resolved]);

  return null;
}

// Prima oprire a tastaturii pe orice pagină: sare peste bara de navigație,
// direct în conținut. Se vede doar când primește focus.
function SkipLink() {
  return (
    <Box
      component="a"
      href="#continut"
      sx={{
        position: 'fixed',
        top: 8,
        left: 8,
        zIndex: 2000,
        transform: 'translateY(-200%)',
        px: 2.5,
        py: 1.5,
        borderRadius: '12px',
        fontFamily: FONT.sans,
        fontSize: 15,
        fontWeight: 600,
        textDecoration: 'none',
        color: 'var(--qf-accent-ink)',
        backgroundColor: 'var(--qf-accent)',
        '&:focus-visible': { transform: 'translateY(0)', outline: '2px solid var(--qf-ink)', outlineOffset: 2 },
      }}
    >
      Sari la conținut
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme} defaultMode="system">
      <CssBaseline />
      <GlobalStyles styles={tokenStyles} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--qf-bg)',
          color: 'var(--qf-ink)',
          ...fullHeight,
        }}
      >
        <Seo />
        <ScrollToTop />
        <SystemChrome />
        <SkipLink />

        {/* Navbar se poziționează singur (AppBar position="fixed"). */}
        <Navbar />

        {/* Coloană flex, ca pagina dinăuntru să poată umple spațiul rămas până
            la footer fără să-și ceară ea `100dvh`. */}
        <Box
          component="main"
          id="continut"
          sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/services" element={<Services />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/contact" element={<Contact />} />

            {/* Orice altă adresă: o pagină care spune ce s-a întâmplat, nu un
                ecran gol între meniu și footer. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Box>

        <Footer />
      </Box>
    </ThemeProvider>
  );
}

export default App;
